import { useState } from "react";
import LockPersonIcon from "@mui/icons-material/LockPerson";
import { Avatar, Box, Button, Container, Grid, TextField, Typography, useTheme } from "@mui/material";

import {
  COMPONENT_STYLE,
  CRPS_ORANGE_DARK,
  CRPS_ORANGE_LIGHT,
  fetchAuthLogin,
  PG_ERR_MSG,
  SetSnackType,
  timeout,
} from "../../utils";
import { DialogForgotPw, DialogRegister, Password } from ".";

export const Login = (props: { connect: (username: string) => void; setSnack: SetSnackType }) => {
  const theme = useTheme();

  const [loggingIn, setLoggingIn] = useState(false);
  const [openRegister, setOpenRegister] = useState(false);
  const [openForgotPwOpen, setOpenForgotPw] = useState(false);

  const [un, setUn] = useState("");
  const [unErr, setUnErr] = useState(false);
  const [pw, setPw] = useState("");
  const [pwErr, setPwErr] = useState(false);

  const handleLogIn = async () => {
    setLoggingIn(true);
    try {
      const { error, username } = await fetchAuthLogin(un, pw);
      if (error || !username) {
        if (error?.includes("password")) {
          setPwErr(true);
          props.setSnack(error, "warning");
        } else {
          setUnErr(true);
          props.setSnack(error ?? PG_ERR_MSG, "error");
        }
        return;
      }
      props.setSnack("Logged in", "success");
      props.connect(username);
      setPw("");
    } catch (err: any) {
      props.setSnack(err.error ?? err.toString(), "error");
    } finally {
      timeout(600).then(() => setLoggingIn(false)); // before unlocking Sign In button
    }
  };

  const registered = (regUn: string, regPw: string) => {
    setUnErr(false);
    setPwErr(false);
    setUn(regUn);
    setPw(regPw);
    props.setSnack(`Registered: ${regUn}`, "success");
  };

  const reRegistered = (regUn: string, regPw: string) => {
    setUnErr(false);
    setPwErr(false);
    setUn(regUn);
    setPw(regPw);
    props.setSnack(`New password for ${regUn}`, "success");
  };

  return (
    <Container sx={COMPONENT_STYLE}>
      <Container component="section" maxWidth="xs">
        <Box
          sx={{
            minHeight: 512,
            marginTop: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Avatar
            sizes="large"
            sx={{ p: 2.6, bgcolor: theme.palette.mode === "light" ? CRPS_ORANGE_LIGHT : CRPS_ORANGE_DARK }}
          >
            <LockPersonIcon fontSize="large" />
          </Avatar>
          <Typography variant="h5" sx={{ my: 4 }}>
            Login to Classroom PowerSorter®
          </Typography>
          <Box sx={{ mt: 1 }}>
            <form>
              <TextField
                autoComplete="username"
                autoFocus
                error={unErr}
                fullWidth
                label="Username"
                margin="normal"
                onChange={ev => {
                  setUnErr(false);
                  setPwErr(false);
                  setUn(ev.target.value);
                }}
                onKeyDownCapture={ev => {
                  if (ev.key === "Enter") handleLogIn();
                }}
                required
                value={un}
              />
              <Password {...{ pw, pwErr, setPw, setPwErr, setSnack: props.setSnack, submit: handleLogIn }} />
            </form>
            <Button
              disabled={!un || !pw}
              fullWidth
              loading={loggingIn}
              onClick={handleLogIn}
              sx={{ mt: 3, mb: 2, py: 1.1 }}
              variant="contained"
            >
              Sign In
            </Button>
            <Grid container>
              <Grid size={6} display="flex" alignItems="flex-start" sx={{ justifyContent: "center" }}>
                <Button disabled={loggingIn} onClick={() => setOpenRegister(true)}>
                  <Typography variant="subtitle2">Register a new account</Typography>
                </Button>
              </Grid>
              <Grid size={6} display="flex" alignItems="flex-start" sx={{ justifyContent: "center" }}>
                <Button disabled={loggingIn} onClick={() => setOpenForgotPw(true)}>
                  <Typography variant="subtitle2">Forgot password?</Typography>
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Box>
        <DialogRegister {...{ open: openRegister, registered, setOpen: setOpenRegister, setSnack: props.setSnack }} />
        <DialogForgotPw
          {...{ open: openForgotPwOpen, reRegistered, setOpen: setOpenForgotPw, setSnack: props.setSnack }}
        />
      </Container>
    </Container>
  );
};
